import React, { useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';

import { CodeSnippet } from '@zurich-zurichat/code-snippet';
import { getCurrentFile, useAppSelector } from 'app/redux';
import styles from './PDFPreview.module.scss';

interface OptionParams {
  displayFile?: File;
  previewStyles?: any;
}

export const PDFPreview: React.FC<OptionParams> = ({
  previewStyles = undefined,
  displayFile = undefined
}): React.ReactElement => {
  const selectedFile = useAppSelector(getCurrentFile);
  const currentFile = displayFile || selectedFile;

  type StringOrBufferOrNull = string | ArrayBuffer | null;
  const audioTypes = new Set([
    'audio/mp3',
    'audio/mp4',
    'audio/m4a',
    'audio/wav',
    'audio/mpeg'
  ]);
  const imageTypes = new Set([
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/bmp',
    'image/tiff',
    'image/heif'
  ]);
  const potentiallyMaliciousFileExtensions = new Set([
    'html',
    'htm',
    'xhtml',
    'xht',
    'xml',
    'css',
    'js',
    'svg',
    'swf'
  ]);

  const handleTextFile = (
    readerResult: StringOrBufferOrNull,
    preview: HTMLIFrameElement | null,
    currentFileType: string
  ): void => {
    if (preview?.parentElement)
      preview.parentElement.innerHTML = ReactDOMServer.renderToString(
        <CodeSnippet
          language={currentFileType === 'application/json' ? 'json' : 'text'}
          code={String(readerResult).trim()}
          className={styles['code-snippet']}
          color="var(--color-primary-data-blue)"
          copy={false}
        />
      );
  };

  const handleImageFile = (
    readerResult: StringOrBufferOrNull,
    preview: HTMLIFrameElement | null,
    currentFileName: string
  ): void => {
    if (preview?.parentElement)
      preview.parentElement.innerHTML = `<img
                  src=${String(readerResult).trim()}
                  alt=${currentFileName}
                  class=${styles['image-preview']}
                />`;
  };

  const handleAudioFile = (
    readerResult: StringOrBufferOrNull,
    preview: HTMLIFrameElement | null,
    currentFileName: string,
    fileType: string
  ): void => {
    if (preview?.parentElement) {
      preview.parentElement.innerHTML = `
                  <audio controls>
                    <source 
                    src=${String(readerResult).trim()}
                    type=${fileType.trim()}
                    alt=${currentFileName}
                    class=${styles['audio-preview']}
                  />
                  Your browser does not support the audio element.
                  </audio>`;
    }
  };

  const handleOtherFile = (
    readerResult: StringOrBufferOrNull,
    preview: HTMLIFrameElement | null
  ): void => {
    if (preview != null) preview.src = readerResult as string;
  };

  useEffect(() => {
    if (currentFile !== undefined) {
      const preview: HTMLIFrameElement | null =
        document.querySelector('iframe');
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        function () {
          const fileExtension = currentFile.name.split('.').pop() || 'unknown';

          if (!potentiallyMaliciousFileExtensions.has(fileExtension)) {
            if (
              ['text/plain', 'application/json'].includes(currentFile?.type)
            ) {
              handleTextFile(reader.result, preview, currentFile.type);
            } else if (imageTypes.has(currentFile?.type)) {
              handleImageFile(reader.result, preview, currentFile.name);
            } else if (audioTypes.has(currentFile?.type)) {
              handleAudioFile(
                reader.result,
                preview,
                currentFile.name,
                currentFile.type
              );
            } else {
              handleOtherFile(reader.result, preview);
            }
          }
        },
        false
      );

      ['text/plain', 'application/json'].includes(currentFile?.type)
        ? reader.readAsText(currentFile)
        : reader.readAsDataURL(currentFile);
    }
  }, [currentFile]);

  if (currentFile === undefined) return <></>;

  return (
    <div className={styles.preview} style={previewStyles}>
      {currentFile !== undefined && (
        <iframe src={undefined} title={currentFile?.name}>
          <p>This browser does not support PDF!</p>
        </iframe>
      )}
    </div>
  );
};
